import { useEffect, useState } from "react";
import { API } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Hash, Save, Loader2, RefreshCw, AlertTriangle, Eye, Calendar, RotateCcw, ChefHat } from "lucide-react";
import { toast } from "sonner";

const PATTERN_TOKENS = [
  { token: "{prefix}", label: "Prefix", example: "SM-" },
  { token: "{serial}", label: "Serial Number", example: "0001" },
  { token: "{year}", label: "Full Year", example: "2026" },
  { token: "{yy}", label: "Short Year", example: "26" },
  { token: "{month}", label: "Month", example: "04" },
  { token: "{day}", label: "Day", example: "15" },
  { token: "{fy}", label: "Financial Year", example: "2627" },
];

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const PRESET_PATTERNS = [
  { label: "Simple: SM-0001", pattern: "{prefix}{serial}" },
  { label: "Yearly: SM-2026-0001", pattern: "{prefix}{year}-{serial}" },
  { label: "Monthly: SM-2026-04-0001", pattern: "{prefix}{year}-{month}-{serial}" },
  { label: "FY Based: SM-FY2627-0001", pattern: "{prefix}FY{fy}-{serial}" },
  { label: "Compact: SM260401", pattern: "{prefix}{yy}{month}{serial}" },
  { label: "Daily: SM-20260415-0001", pattern: "{prefix}{year}{month}{day}-{serial}" },
];

export default function AdminOrderSerials() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reserialing, setReserialing] = useState(false);
  const [showReserialDialog, setShowReserialDialog] = useState(false);
  const [kitchens, setKitchens] = useState([]);
  const [selectedKitchenId, setSelectedKitchenId] = useState("");
  const [serialType, setSerialType] = useState("order"); // "order" or "recharge"

  const [formData, setFormData] = useState({
    prefix: "SM-",
    pattern: "{prefix}{serial}",
    starting_number: 1,
    serial_padding: 4,
    applicable_from: "",
    auto_reset: "never",
    fy_start_month: 1,
  });

  useEffect(() => {
    fetchKitchens();
  }, []);

  useEffect(() => {
    if (serialType === "order") {
      fetchConfig(selectedKitchenId);
    } else {
      fetchRechargeConfig();
    }
  }, [selectedKitchenId, serialType]);

  const fetchKitchens = async () => {
    try {
      const res = await fetch(`${API}/kitchens`, { credentials: "include" });
      if (res.ok) setKitchens(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchConfig = async (kitchenId) => {
    setLoading(true);
    try {
      const url = kitchenId
        ? `${API}/order-serial-config?kitchen_id=${kitchenId}`
        : `${API}/order-serial-config`;
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
        setFormData({
          prefix: data.prefix || "",
          pattern: data.pattern || "{prefix}{serial}",
          starting_number: data.starting_number || 1,
          serial_padding: data.serial_padding || 4,
          applicable_from: data.applicable_from || "",
          auto_reset: data.auto_reset || "never",
          fy_start_month: data.fy_start_month || 1,
        });
      }
    } catch (error) {
      console.error("Failed to fetch serial config:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRechargeConfig = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/recharge-serial-config`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
        setFormData({
          prefix: data.prefix || "WR-",
          pattern: data.pattern || "{prefix}{serial}",
          starting_number: data.starting_number || 1,
          serial_padding: data.serial_padding || 4,
          applicable_from: data.applicable_from || "",
          auto_reset: data.auto_reset || "never",
          fy_start_month: data.fy_start_month || 1,
        });
      }
    } catch (error) {
      console.error("Failed to fetch recharge serial config:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const endpoint = serialType === "recharge" ? `${API}/recharge-serial-config` : `${API}/order-serial-config`;
      const payload = {
        ...formData,
        kitchen_id: serialType === "order" ? (selectedKitchenId || null) : null,
      };
      const response = await fetch(endpoint, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Failed to save");
      const data = await response.json();
      setConfig(data);
      toast.success("Serial number configuration saved!");
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleForceReserial = async () => {
    setReserialing(true);
    try {
      let url;
      if (serialType === "recharge") {
        url = `${API}/recharge-serial-config/force-reserial`;
      } else {
        url = selectedKitchenId
          ? `${API}/order-serial-config/force-reserial?kitchen_id=${selectedKitchenId}`
          : `${API}/order-serial-config/force-reserial`;
      }
      const response = await fetch(url, {
        method: "POST",
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to reserial");
      const data = await response.json();
      toast.success(data.message);
      setShowReserialDialog(false);
      if (serialType === "recharge") {
        fetchRechargeConfig();
      } else {
        fetchConfig(selectedKitchenId);
      }
    } catch (error) {
      console.error("Reserial error:", error);
      toast.error("Failed to reserial");
    } finally {
      setReserialing(false);
    }
  };

  const generatePreview = () => {
    const now = new Date();
    const counter = formData.starting_number;
    const serial = String(counter).padStart(formData.serial_padding, "0");
    const year = String(now.getFullYear());
    const yy = String(now.getFullYear() % 100).padStart(2, "0");
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");

    const fyMonth = formData.fy_start_month;
    const fyStartYear = now.getMonth() + 1 >= fyMonth ? now.getFullYear() : now.getFullYear() - 1;
    const fyEndYear = fyStartYear + 1;
    const fy = fyMonth === 1
      ? String(now.getFullYear())
      : `${String(fyStartYear % 100).padStart(2, "0")}${String(fyEndYear % 100).padStart(2, "0")}`;

    let result = formData.pattern;
    const replacements = {
      "{prefix}": formData.prefix,
      "{serial}": serial,
      "{year}": year,
      "{yy}": yy,
      "{month}": month,
      "{mm}": month,
      "{day}": day,
      "{dd}": day,
      "{fy}": fy,
    };
    for (const [token, value] of Object.entries(replacements)) {
      result = result.split(token).join(value);
    }
    return result;
  };

  const insertToken = (token) => {
    setFormData((prev) => ({ ...prev, pattern: prev.pattern + token }));
  };

  const selectedKitchenName = selectedKitchenId
    ? kitchens.find((k) => k.kitchen_id === selectedKitchenId)?.kitchen_code || ""
    : "Global (Default)";

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-green-500" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="animate-fade-in max-w-3xl" data-testid="admin-order-serials">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Invoice Serial Numbers</h1>
            <p className="text-sm text-gray-500 mt-1">Configure how order and recharge invoice numbers are generated</p>
          </div>
          <div className="flex gap-2">
            <Dialog open={showReserialDialog} onOpenChange={setShowReserialDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="gap-2 border-orange-300 text-orange-600 hover:bg-orange-50" data-testid="force-reserial-btn">
                  <RotateCcw size={16} /> Force Reserial
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-orange-600">
                    <AlertTriangle size={20} /> Force Reserial {serialType === "recharge" ? "Recharge Invoices" : (selectedKitchenId ? selectedKitchenName : "All") + " Orders"}
                  </DialogTitle>
                  <DialogDescription>
                    {serialType === "recharge"
                      ? "This will renumber ALL paid wallet recharge invoices using the current serial pattern. This action cannot be undone."
                      : `This will renumber ${selectedKitchenId ? `orders for kitchen ${selectedKitchenName}` : "ALL orders"} using the current serial pattern. This action cannot be undone.`}
                  </DialogDescription>
                </DialogHeader>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-sm text-orange-700">
                  <p className="font-medium mb-2">What will happen:</p>
                  <ul className="list-disc ml-4 space-y-1">
                    <li>{serialType === "recharge" ? "All paid recharge transactions" : (selectedKitchenId ? `Orders for ${selectedKitchenName}` : "All orders")} will be sorted by creation date</li>
                    <li>New serial numbers will be assigned starting from #{formData.starting_number}</li>
                    <li>The current counter will be updated to match</li>
                  </ul>
                </div>
                <div className="flex gap-3 mt-4">
                  <Button variant="outline" className="flex-1" onClick={() => setShowReserialDialog(false)}>Cancel</Button>
                  <Button className="flex-1 bg-orange-500 hover:bg-orange-600 text-white" onClick={handleForceReserial} disabled={reserialing} data-testid="confirm-reserial-btn">
                    {reserialing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : <><RotateCcw size={16} className="mr-2" /> Confirm Reserial</>}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Button className="bg-green-500 hover:bg-green-600 gap-2" onClick={handleSave} disabled={saving} data-testid="save-serial-config-btn">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <><Save size={16} /> Save Configuration</>}
            </Button>
          </div>
        </div>

        {/* Type Tabs */}
        <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl" data-testid="serial-type-tabs">
          <button className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${serialType === "order" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`} onClick={() => setSerialType("order")} data-testid="tab-order-serials">
            Order Invoices
          </button>
          <button className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${serialType === "recharge" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`} onClick={() => setSerialType("recharge")} data-testid="tab-recharge-serials">
            Wallet Recharge Invoices
          </button>
        </div>

        {/* Kitchen Selector - only for Order Invoices */}
        {serialType === "order" && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-6" data-testid="kitchen-serial-selector">
          <div className="flex items-center gap-3 mb-3">
            <ChefHat size={20} className="text-green-500" />
            <h2 className="text-lg font-semibold text-gray-900">Select Kitchen</h2>
          </div>
          <p className="text-sm text-gray-500 mb-3">Each kitchen can have its own serial number pattern and counter. Select a kitchen to configure, or use "Global" as the default for kitchens without a specific config.</p>
          <Select value={selectedKitchenId || "global"} onValueChange={(v) => setSelectedKitchenId(v === "global" ? "" : v)}>
            <SelectTrigger className="bg-white" data-testid="kitchen-serial-select">
              <SelectValue placeholder="Select kitchen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="global">Global (Default Fallback)</SelectItem>
              {kitchens.map((k) => (
                <SelectItem key={k.kitchen_id} value={k.kitchen_id}>
                  {k.kitchen_code} - {k.city || k.locality || ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        )}
        {/* Live Preview */}
        <div className="bg-gray-900 text-white rounded-2xl p-6 mb-6" data-testid="serial-preview">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Eye size={16} className="text-green-400" />
              <span className="text-sm text-gray-400 font-medium">Live Preview</span>
            </div>
            {selectedKitchenId && (
              <span className="text-xs bg-green-900/50 text-green-400 px-2 py-1 rounded-full">{selectedKitchenName}</span>
            )}
          </div>
          <p className="text-3xl font-mono font-bold tracking-wider" data-testid="serial-preview-value">
            {generatePreview()}
          </p>
          <p className="text-[10px] text-gray-400 mt-2">
            Next order will use this number. Current counter: {config?.current_counter || 0}
            {selectedKitchenId && config?.config_id?.startsWith("serial_config_") ? "" : selectedKitchenId ? " (using global fallback)" : ""}
          </p>
        </div>

        {/* Configuration Form */}
        <div className="space-y-6">
          {/* Prefix & Pattern */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Hash size={20} className="text-green-500" />
              Serial Pattern
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <Label>Prefix</Label>
                <Input value={formData.prefix} onChange={(e) => setFormData({ ...formData, prefix: e.target.value })} placeholder="e.g., SM-, ORD-, INV-" className="bg-white font-mono" data-testid="serial-prefix" />
                <p className="text-[10px] text-gray-500 mt-1">Added at the start of each order number</p>
              </div>
              <div>
                <Label>Serial Padding (digits)</Label>
                <Select value={String(formData.serial_padding)} onValueChange={(v) => setFormData({ ...formData, serial_padding: parseInt(v) })}>
                  <SelectTrigger className="bg-white" data-testid="serial-padding"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[2, 3, 4, 5, 6, 7, 8].map((n) => (
                      <SelectItem key={n} value={String(n)}>{n} digits ({"0".repeat(n - 1)}1)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Pattern Builder */}
            <div className="mb-4">
              <Label>Pattern</Label>
              <Input value={formData.pattern} onChange={(e) => setFormData({ ...formData, pattern: e.target.value })} placeholder="{prefix}{year}-{serial}" className="bg-white font-mono mb-2" data-testid="serial-pattern" />
              <div className="flex flex-wrap gap-1.5">
                {PATTERN_TOKENS.map((t) => (
                  <button key={t.token} className="px-2.5 py-1 bg-gray-100 hover:bg-green-100 text-gray-600 hover:text-green-700 rounded-lg text-xs font-mono transition-colors" onClick={() => insertToken(t.token)} title={`${t.label} (e.g., ${t.example})`} data-testid={`token-${t.token.replace(/[{}]/g, "")}`}>
                    {t.token}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-gray-500 mt-2">Click tokens above to insert them into the pattern</p>
            </div>

            {/* Preset Patterns */}
            <div>
              <Label className="text-[10px] text-gray-400">Quick Presets</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                {PRESET_PATTERNS.map((preset) => (
                  <button key={preset.pattern} className={`text-left px-3 py-2 rounded-lg border text-sm transition-colors ${formData.pattern === preset.pattern ? "border-green-500 bg-green-50 text-green-700" : "border-gray-200 hover:border-gray-300 text-gray-600"}`} onClick={() => setFormData({ ...formData, pattern: preset.pattern })} data-testid={`preset-${preset.pattern.replace(/[{}-]/g, "").toLowerCase()}`}>
                    <span className="font-medium">{preset.label}</span>
                    <br />
                    <span className="font-mono text-[10px] text-gray-500">{preset.pattern}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Starting Number & Applicable From */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar size={20} className="text-green-500" />
              Counter Settings
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Starting Number</Label>
                <Input type="number" min="1" value={formData.starting_number} onChange={(e) => setFormData({ ...formData, starting_number: parseInt(e.target.value) || 1 })} className="bg-white" data-testid="serial-starting-number" />
                <p className="text-[10px] text-gray-500 mt-1">Serial counting starts from this number</p>
              </div>
              <div>
                <Label>Applicable From Date</Label>
                <Input type="date" value={formData.applicable_from} onChange={(e) => setFormData({ ...formData, applicable_from: e.target.value })} className="bg-white" data-testid="serial-applicable-from" />
                <p className="text-[10px] text-gray-500 mt-1">New pattern applies from this date</p>
              </div>
            </div>
          </div>

          {/* Auto Reset & Financial Year */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <RefreshCw size={20} className="text-green-500" />
              Auto Reset & Financial Year
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Auto Reset Serial Counter</Label>
                <Select value={formData.auto_reset} onValueChange={(v) => setFormData({ ...formData, auto_reset: v })}>
                  <SelectTrigger className="bg-white" data-testid="serial-auto-reset"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="never">Never (continuous numbering)</SelectItem>
                    <SelectItem value="monthly">Monthly (resets every month)</SelectItem>
                    <SelectItem value="yearly">Yearly (resets every financial year)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-gray-500 mt-1">
                  {formData.auto_reset === "never" && "Serial number will keep incrementing forever"}
                  {formData.auto_reset === "monthly" && "Counter resets to starting number on the 1st of each month"}
                  {formData.auto_reset === "yearly" && "Counter resets at the start of each financial year"}
                </p>
              </div>
              <div>
                <Label>Financial Year Starts In</Label>
                <Select value={String(formData.fy_start_month)} onValueChange={(v) => setFormData({ ...formData, fy_start_month: parseInt(v) })}>
                  <SelectTrigger className="bg-white" data-testid="serial-fy-month"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTH_NAMES.map((name, idx) => (
                      <SelectItem key={idx + 1} value={String(idx + 1)}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-gray-500 mt-1">
                  {formData.fy_start_month === 1 && "Calendar year (Jan-Dec) - Most countries"}
                  {formData.fy_start_month === 4 && "Apr-Mar - India, UK, Japan"}
                  {formData.fy_start_month === 7 && "Jul-Jun - Australia, New Zealand"}
                  {formData.fy_start_month === 10 && "Oct-Sep - USA (Federal)"}
                  {![1, 4, 7, 10].includes(formData.fy_start_month) && `${MONTH_NAMES[formData.fy_start_month - 1]} to ${MONTH_NAMES[(formData.fy_start_month - 2 + 12) % 12]}`}
                </p>
              </div>
            </div>

            {formData.auto_reset !== "never" && (
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                <p className="font-medium">How it works:</p>
                <p className="mt-1">
                  {formData.auto_reset === "monthly"
                    ? `The serial counter will reset to ${formData.starting_number} at the beginning of each month. Use {month} in your pattern to distinguish months.`
                    : `The serial counter will reset to ${formData.starting_number} at the start of each financial year (${MONTH_NAMES[formData.fy_start_month - 1]}). Use {fy} in your pattern to distinguish years.`
                  }
                </p>
              </div>
            )}
          </div>

          {/* Pattern Reference */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Token Reference</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-2 text-gray-500 font-medium">Token</th>
                    <th className="text-left py-2 text-gray-500 font-medium">Description</th>
                    <th className="text-left py-2 text-gray-500 font-medium">Example</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {PATTERN_TOKENS.map((t) => (
                    <tr key={t.token}>
                      <td className="py-2 font-mono text-green-600">{t.token}</td>
                      <td className="py-2 text-gray-600">{t.label}</td>
                      <td className="py-2 font-mono text-gray-400">{t.example}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
